import { API_URL, formatApiError } from '../utils/api';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { DataTable } from '../components/DataTable';
import { Plus, Wallet, ArrowDown, ArrowUp, Spinner } from '@phosphor-icons/react';
import { toast } from 'sonner';

const FLOAT_TABS = [
  { key: 'card_float', label: 'Card Balance', icon: Wallet, color: '#0A2540' },
  { key: 'agent_float', label: 'Float', icon: Wallet, color: '#10B981' },
];

export default function FloatLedgerPage() {
  const { isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('card_float');
  const [entries, setEntries] = useState([]);
  const [balances, setBalances] = useState([]);
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEntity, setFilterEntity] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    entity_id: '', action: 'deposit', amount: '', notes: '', reference: '',
  });

  const hdrs = () => {
    const t = localStorage.getItem('access_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };

  const fetchEntities = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/entities`, { headers: hdrs() });
      setEntities(data || []);
    } catch (e) { /* ignore */ }
  }, []);

  // GL transactions from middleware. Only fetched when an entity is selected.
  const fetchEntries = useCallback(async () => {
    if (!filterEntity || filterEntity === 'all') { setEntries([]); setLoading(false); return; }
    setLoading(true);
    try {
      const glType = activeTab === 'card_float' ? 'CARD' : 'FLOAT';
      const { data } = await axios.get(
        `${API_URL}/api/float-ledger/transactions?entity_id=${filterEntity}&gl_type=${glType}`,
        { headers: hdrs() }
      );
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      setEntries([]);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [activeTab, filterEntity]);

  // Live middleware balances (only fetched when a single entity is selected, since
  // currencies differ across entities — cross-entity totals are not meaningful).
  const fetchMiddlewareBalances = useCallback(async () => {
    if (!filterEntity || filterEntity === 'all') { setBalances([]); return; }
    try {
      const { data } = await axios.get(
        `${API_URL}/api/float-ledger/middleware-balances?entity_id=${filterEntity}`,
        { headers: hdrs() }
      );
      setBalances(Array.isArray(data) ? data : []);
    } catch (e) { setBalances([]); }
  }, [filterEntity]);

  useEffect(() => { fetchEntities(); }, [fetchEntities]);
  useEffect(() => { fetchEntries(); fetchMiddlewareBalances(); }, [fetchEntries, fetchMiddlewareBalances]);

  const openDialog = (action) => {
    setForm({ entity_id: '', action, amount: '', notes: '', reference: '' });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.entity_id) { toast.error('Select an entity'); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error('Amount must be greater than zero'); return; }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/float-ledger`, {
        entity_id: form.entity_id,
        float_type: activeTab,
        action: form.action,
        amount: Number(form.amount),
        notes: form.notes || null,
        reference: form.reference || null,
      }, { headers: hdrs() });
      toast.success(`${form.action === 'deposit' ? 'Balance added' : 'Balance deducted'} successfully`);
      setDialogOpen(false);
      await Promise.all([fetchEntries(), fetchMiddlewareBalances()]);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return <div className="p-8 text-center text-[#78716C]">Only Super Admin can access the Float Ledger.</div>;
  }

  // Live middleware balance(s) for the selected entity, filtered to active tab's gl_type
  const activeGlType = activeTab === 'card_float' ? 'CARD' : 'FLOAT';
  const liveBalances = balances.filter(b => String(b.gl_type || '').toUpperCase() === activeGlType);
  const activeMeta = FLOAT_TABS.find(t => t.key === activeTab);

  return (
    <div className="space-y-6" data-testid="float-ledger-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Float Ledger</h1>
          <p className="text-sm text-[#78716C]">Manage Card Balance and Float allocations across entities</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => openDialog('withdrawal')}
            variant="outline"
            className="border-[#EDE6D8] rounded-lg"
            data-testid="btn-deduct-float"
          >
            <ArrowDown size={16} className="mr-2" /> Deduct Float
          </Button>
          <Button
            onClick={() => openDialog('deposit')}
            className="bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="btn-add-float"
          >
            <Plus size={16} className="mr-2" /> Add Float
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-[#FFFFFF] border border-[#EDE6D8] rounded-xl overflow-hidden">
        <div className="flex items-center gap-1 px-2 py-2 border-b border-[#EDE6D8] bg-[#FCFAF6]/50 overflow-x-auto">
          {FLOAT_TABS.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`px-5 py-2.5 text-sm font-semibold rounded-lg whitespace-nowrap transition-all flex items-center gap-2 ${
                  active
                    ? 'bg-[#FFFFFF] text-[#1C1917] shadow-sm border border-[#EDE6D8]'
                    : 'text-[#78716C] hover:text-[#1C1917] hover:bg-[#FFFFFF]/60'
                }`}
              >
                <Icon size={16} weight={active ? 'fill' : 'regular'} style={active ? { color: t.color } : {}} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Filter row + live entity balance (only when an entity is selected) */}
        <div className="p-5 border-b border-[#EDE6D8] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4 min-h-[48px]">
            {filterEntity === 'all' ? (
              <p className="text-xs text-[#78716C] italic">Select an entity to view its live {activeMeta.label.toLowerCase()} balance from middleware.</p>
            ) : liveBalances.length === 0 ? (
              <p className="text-xs text-[#78716C] italic">No {activeMeta.label.toLowerCase()} GL set up for this entity on middleware.</p>
            ) : (
              liveBalances.map((b, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`live-balance-${i}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${activeMeta.color}15` }}>
                    <Wallet size={18} weight="fill" style={{ color: activeMeta.color }} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.12em] uppercase text-[#78716C] font-semibold">
                      {b.gl_name || `${activeMeta.label} GL`}{b.gl_code ? ` · ${b.gl_code}` : ''}
                    </p>
                    <p className="text-xl font-bold font-mono tracking-tight" style={{ color: activeMeta.color }}>
                      {activeTab === 'card_float'
                        ? Number(b.balance).toLocaleString(undefined, { maximumFractionDigits: 0 })
                        : Number(b.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      {activeTab !== 'card_float' && b.currency
                        ? <span className="text-xs text-[#78716C] ml-2 font-sans">{b.currency}</span>
                        : null}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-[#78716C]">Entity</Label>
            <Select value={filterEntity} onValueChange={setFilterEntity}>
              <SelectTrigger className="w-[260px] border-[#EDE6D8] rounded-lg" data-testid="filter-entity">
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                {entities.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Ledger table */}
        <div className="p-5">
          {(!filterEntity || filterEntity === 'all') && !loading ? (
            <div className="py-16 text-center text-[#78716C] text-sm">
              Select an entity above to view its <span className="font-semibold text-[#1C1917]">{activeMeta.label}</span> transaction history from the middleware.
            </div>
          ) : (
            <DataTable
              columns={[
                { key: 'trn_date', label: 'Date', width: 'minmax(120px,1fr)', mono: true, tone: 'muted',
                  render: (e) => e.trn_date || '—' },
                { key: 'gl', label: 'GL', width: 'minmax(160px,1.4fr)', render: (e) => (
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-[#1C1917] group-hover:text-[#1C1917]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      {e.gl_name || '—'}
                    </span>
                    <span className="block truncate text-[10px] text-[#78716C] group-hover:text-[#78716C] font-mono">
                      {e.gl_code || ''}
                    </span>
                  </span>
                ) },
                { key: 'action', label: 'Action', width: 'minmax(90px,0.8fr)', render: (e) => (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wider ${
                    e.action === 'deposit' ? 'bg-emerald-500/10 text-emerald-700' : 'bg-amber-500/10 text-amber-700'
                  } group-hover:bg-[#EDE6D8] group-hover:text-[#1C1917]`}>
                    {e.action === 'deposit' ? <ArrowUp size={10} /> : <ArrowDown size={10} />}
                    {e.action === 'deposit' ? 'Add' : 'Deduct'}
                  </span>
                ) },
                { key: 'amount', label: 'Amount', width: 'minmax(120px,1fr)', mono: true, align: 'right', tone: 'strong',
                  render: (e) => {
                    const n = Number(e.amount || 0);
                    if (activeTab === 'card_float') return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
                    return `${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${e.currency ? ' ' + e.currency : ''}`;
                  } },
                { key: 'open_balance', label: 'Open', width: 'minmax(120px,1fr)', mono: true, align: 'right', tone: 'muted',
                  render: (e) => {
                    const n = Number(e.open_balance || 0);
                    return activeTab === 'card_float'
                      ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
                      : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  } },
                { key: 'close_balance', label: 'Close', width: 'minmax(120px,1fr)', mono: true, align: 'right', tone: 'strong',
                  render: (e) => {
                    const n = Number(e.close_balance || 0);
                    return activeTab === 'card_float'
                      ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
                      : n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  } },
                { key: 'narration', label: 'Narration', width: 'minmax(200px,2fr)', tone: 'muted' },
                { key: 'maker_name', label: 'Maker', width: 'minmax(120px,1fr)', tone: 'strong',
                  render: (e) => e.maker_name || e.maker_id || '—' },
              ]}
              rows={entries}
              loading={loading}
              rowKey={(e, i) => `${e.trn_date || ''}-${e.gl_code || ''}-${i}`}
              emptyIcon={<Wallet size={20} className="text-slate-500" />}
              emptyTitle={`No ${activeMeta.label.toLowerCase()} transactions`}
              emptyText="No transactions found for this entity yet."
              testIdPrefix="ledger"
            />
          )}
        </div>
      </div>

      {/* Add / Withdraw Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {form.action === 'deposit' ? 'Add' : 'Deduct'} {activeMeta.label}
            </DialogTitle>
            <DialogDescription>
              {form.action === 'deposit'
                ? `Allocate ${activeMeta.label.toLowerCase()} to an entity.`
                : `Deduct ${activeMeta.label.toLowerCase()} from an entity. Cannot exceed available balance.`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label>Entity <span className="text-[#EF4444]">*</span></Label>
              <Select value={form.entity_id} onValueChange={(v) => setForm(p => ({ ...p, entity_id: v }))}>
                <SelectTrigger className="border-[#EDE6D8] rounded-lg" data-testid="dialog-entity-select">
                  <SelectValue placeholder="Select entity" />
                </SelectTrigger>
                <SelectContent>
                  {entities.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Amount <span className="text-[#EF4444]">*</span></Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={form.amount}
                onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))}
                placeholder="0.00"
                className="border-[#EDE6D8] rounded-lg font-mono"
                data-testid="dialog-amount"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Reference (optional)</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm(p => ({ ...p, reference: e.target.value }))}
                placeholder="e.g. cheque #, bank txn ref"
                className="border-[#EDE6D8] rounded-lg"
                data-testid="dialog-reference"
              />
            </div>

            <div className="space-y-2">
              <Label>Notes (optional)</Label>
              <textarea
                value={form.notes}
                onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                placeholder="Reason or context for this entry"
                className="w-full border border-[#EDE6D8] rounded-lg p-2 text-sm min-h-[70px] resize-y focus:outline-none focus:ring-1 focus:ring-[#0A2540]"
                data-testid="dialog-notes"
              />
            </div>

            <DialogFooter className="pt-2 border-t border-[#EDE6D8]">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-[#EDE6D8] rounded-lg">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className={`text-white rounded-lg ${form.action === 'deposit' ? 'bg-[#0A2540] hover:bg-[#0A2540]/90' : 'bg-amber-600 hover:bg-amber-700'}`}
                data-testid="dialog-submit"
              >
                {submitting ? <Spinner size={16} className="animate-spin mr-2" /> : null}
                {form.action === 'deposit' ? 'Add Float' : 'Deduct Float'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
